import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { IAnnouncement } from 'announcements/types';
import styled from 'styled-components';
import { Banner } from 'components/Banner';
import { AnalyticsContext } from 'contexts/analytics';
import { FullStoryContext } from 'contexts/fullstory';
import { theme, toRem } from 'design';
import AnnouncementService from 'lib/AnnouncementService';
import { selectCurrentEmployee } from 'store/App';
import { selectCurrentEmployeeRole } from 'store/Employers';
import { selectCurrentUser } from 'store/User/selectors';
import { SiteRole } from 'types';
import AnnouncementModal from './AnnouncementModal';

interface IProps {
  announcements?: IAnnouncement[];
}

const StyledBanner = styled(Banner)`
  &:hover {
    cursor: pointer;
    background-color: ${theme.color.primary[600]};
  }
`;

const BannerInner = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: ${toRem(1200)};
  gap: ${toRem(theme.space.md)};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  width: 100%;
  gap: ${toRem(theme.space['2xs'])};
  text-align: center;
  padding: ${toRem(theme.space.xs)} 0;

  @media (min-width: ${theme.breakpoint.small}px) {
    gap: ${toRem(theme.space.xs)};
  }
`;

const Title = styled.span`
  font-weight: ${theme.font.weight.semiBold};
`;

const AnnouncementBanner: React.FC<IProps> = ({ announcements }: IProps) => {
  const analytics = useContext(AnalyticsContext);
  const fullStory = useContext(FullStoryContext);
  const currentUser = useSelector(selectCurrentUser());
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentRole = useSelector(selectCurrentEmployeeRole);
  const bannerRole = currentRole ?? SiteRole.EMPLOYEE;
  const [dismissedVersion, setDismissedVersion] = useState(0);
  const shownRef = useRef<Set<string>>(new Set());

  const userId = currentUser?.id;

  const activeAnnouncement = useMemo(() => AnnouncementService.getActiveAnnouncement(announcements, bannerRole, userId), [announcements, bannerRole, dismissedVersion, userId]);

  const trackAnnouncementEvent = useCallback(
    (eventName: 'announcement_shown' | 'announcement_clicked', fullStoryName: 'Announcement Shown' | 'Announcement Clicked') => {
      if (!activeAnnouncement) return;

      const organizationId = currentEmployee?.organizationId;
      const siteId = currentEmployee?.siteId ?? 'org';

      analytics?.fireEvent(eventName, {
        announcementId: activeAnnouncement.id,
        organizationId,
        siteId,
        role: bannerRole,
      });

      fullStory?.trackEvent({
        name: fullStoryName,
        properties: {
          announcementId: activeAnnouncement.id,
          organizationId,
          siteId,
          role: bannerRole,
        },
      });
    },
    [activeAnnouncement, analytics, bannerRole, currentEmployee, fullStory]
  );

  useEffect(() => {
    if (!userId || !activeAnnouncement) return;

    const shownKey = `banner::user::${userId}::${activeAnnouncement.id}`;
    if (shownRef.current.has(shownKey)) return;
    shownRef.current.add(shownKey);
    trackAnnouncementEvent('announcement_shown', 'Announcement Shown');
  }, [activeAnnouncement, trackAnnouncementEvent, userId]);

  const [showAnnouncementModal, hideAnnouncementModal] = useModal(() => {
    const handleDismiss = () => {
      AnnouncementService.dismiss(userId, [activeAnnouncement.id]);
      setDismissedVersion((prev) => prev + 1);
      hideAnnouncementModal();
    };

    if (!activeAnnouncement) return null;

    return <AnnouncementModal announcement={activeAnnouncement} isActive onClose={hideAnnouncementModal} onDismiss={handleDismiss} />;
  }, [activeAnnouncement, userId]);

  if (!userId || !activeAnnouncement) return null;

  const handleBannerClick = () => {
    trackAnnouncementEvent('announcement_clicked', 'Announcement Clicked');
    showAnnouncementModal();
  };

  return (
    <StyledBanner dataCy="announcement-banner" appearance="primary" onClick={handleBannerClick}>
      <BannerInner>
        <TextWrapper>
          <Title>{activeAnnouncement.bannerTitle}</Title>
        </TextWrapper>
      </BannerInner>
    </StyledBanner>
  );
};

export default AnnouncementBanner;
